import { iframeResizer } from "iframe-resizer";
import { parse, stringify } from "tiny-querystring";
import scroll from "simplesmoothscroll";
import { detect } from "detect-browser";
import { getLoaderHtml } from "./loaderHtml";
import { getOldBrowserHtml } from "./oldBrowserHtml";

const isDevelopment = process.env.NODE_ENV === "development";
const baseUrl = isDevelopment ? "http://localhost:3000/" : process.env.BASE_URI;

function ready(fn) {
  if (document.readyState != "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

function getPosition(element) {
  var xPosition = 0;
  var yPosition = 0;

  while (element) {
    xPosition += element.offsetLeft - element.scrollLeft + element.clientLeft;
    yPosition += element.offsetTop - element.scrollTop + element.clientTop;
    element = element.offsetParent;
  }

  return { x: xPosition, y: yPosition };
}

function createIframe(url, node, onFinished) {
  // Create iframe
  const iframe = document.createElement("iframe");
  iframe.setAttribute("id", "plan2bookIframe");
  iframe.setAttribute("src", url);
  iframe.setAttribute(
    "sandbox",
    "allow-top-navigation allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
  );
  // iframe.setAttribute("frameborder", 0);
  iframe.style.cssText = "visibility: hidden; display: none;";

  const handleIframeLoaded = () => {
    iframe.removeEventListener("load", handleIframeLoaded);
    // Use small delay to allow for settling
    setTimeout(() => {
      iframe.style.cssText =
        "width: 1px; min-width: 100%; border: 0px; min-height: 568px;";
      onFinished();
    }, 250);
  };
  iframe.addEventListener("load", handleIframeLoaded);

  // Insert iFrame behind the javascript tag
  node.appendChild(iframe);
  // Initialise the iFrameResizer
  iframeResizer(
    {
      log: isDevelopment,
      onScroll: ({ x, y }) => {
        const iframePosition = getPosition(iframe);
        scroll(iframe, {
          offset: -(y - iframePosition.y - 20),
          getEasingFunction: function (targetPosition) {
            return function (stepCount) {
              return stepCount * 0.08;
            };
          },
        });
        return false;
      },
    },
    iframe
  );
}

function load(customerId, language, params, node, onFinished) {
  const pollId = params.pollId;
  const adminId = params.adminId;

  let url = baseUrl;
  const parameters = {
    tenant: customerId,
    lang: language,
  };

  if (params.forceLogo === "true") {
    parameters.forceLogo = "true";
  }

  if (adminId) {
    // We need to go to the admin page
    url += `poll/${pollId}`;
    parameters.admin = adminId;
  } else if (pollId) {
    // Go to the participate page
    url += `poll/${pollId}/participate`;
  } else if (params.startPlanning) {
    // Immediately go to planner
    url += `planner`;
  } else if (params.startBooking) {
    // Immediately go to booker
    url += `booker`;
  } else {
    // Go to home
  }

  if (screen.width <= 500) {
    // Redirect for small screens
    window.location.href = `${url}?${stringify(parameters)}`;
  } else {
    // Load in iFrame
    createIframe(`${url}?${stringify(parameters)}`, node, onFinished);
  }
}

ready(() => {
  // Find our embedding tag
  const scriptTag = document.querySelector("script#plan2book");
  const node = scriptTag.parentNode;

  // Get the query parameters
  const src = scriptTag.src;
  const query = src.split("?")[1];
  const queryParams = parse(query);

  // Get the attribute parameters
  const tagParams = {
    startPlanning:
      scriptTag.dataset.start && scriptTag.dataset.start === "planning"
        ? "true"
        : undefined,
    startBooking:
      scriptTag.dataset.start && scriptTag.dataset.start === "booking"
        ? "true"
        : undefined,
    forceLogo: scriptTag.dataset.forceLogo,
    lang: scriptTag.dataset.lang,
    customer: scriptTag.dataset.customer,
  };

  // Get the page parameters
  let searchString = (location.search || "").substr(1);
  const pageParams = parse(searchString);

  // Now merge everything together to get the final configuration
  const params = {
    ...pageParams, // get ALL the query parameters, there are some that are only used for specific flows
    // Overwrite the ones we know with the most usefull data we can find
    startPlanning:
      tagParams.startPlanning ||
      queryParams.startPlanning ||
      pageParams.startPlanning,
    startBooking:
      tagParams.startBooking ||
      queryParams.startBooking ||
      pageParams.startBooking,
    forceLogo:
      tagParams.forceLogo || queryParams.forceLogo || pageParams.forceLogo,
  };

  const customerId =
    tagParams.customer || queryParams.customer || pageParams.customer;
  const language = tagParams.lang || queryParams.lang || pageParams.lang;

  // Detect old browsers
  const browserResult = detect();

  if (browserResult && browserResult.name === "ie") {
    const oldBrowser = document.createElement("div");
    oldBrowser.style.cssText =
      'font-family: "open sans","Segoe UI",segoe,arial,Verdana,sans-serif; font-weight: 400; font-size: 16px; line-height: 22px; color: rgb(67,56,47); height: 568px; background: #ffffff;';

    let title = "Outdated browser";
    let body =
      'Your browser does not support the planningstool, <a class="link" href="http://browserupdate.org/update-browser.html">update your browser</a>';

    switch (language) {
      case "nl": {
        title = "Verouderde browser";
        body =
          'Je browser ondersteunt de planningstool niet, <a class="link" href="http://browserupdate.org/update-browser.html">update je browser</a>';
        break;
      }
      case "fr": {
        title = "Navigateur obsolète";
        body = `Votre navigateur ne prend pas en charge l'outil de planification, <a class="link" href="http://browserupdate.org/update-browser.html">mettez à jour votre navigateur</a>`;
        break;
      }
      case "de": {
        title = "Veralteter Browser";
        body = `Ihr Browser unterstützt das Planungstool nicht, <a class="link" href="http://browserupdate.org/update-browser.html">Aktualisieren Ihren Browser</a> `;
        break;
      }
    }

    oldBrowser.innerHTML = getOldBrowserHtml(title, body);
    node.appendChild(oldBrowser);
    return;
  }

  // Load the application
  const loader = document.createElement("div");
  loader.style.cssText =
    'font-family: "open sans","Segoe UI",segoe,arial,Verdana,sans-serif; font-weight: 400; font-size: 16px; line-height: 22px; color: rgb(67,56,47); height: 568px; background: #ffffff;';

  let title = "Please wait";
  let body = "We are retrieving all data";

  switch (language) {
    case "nl": {
      title = "Even geduld";
      body = "We halen alle gegevens op";
      break;
    }
    case "fr": {
      title = "Patientez un moment";
      body = "Nous récupérons toutes les données";
      break;
    }
    case "de": {
      title = "Bitte warten";
      body = "Wir rufen alle Daten ab";
      break;
    }
  }

  loader.innerHTML = getLoaderHtml(title, body);
  node.appendChild(loader);

  // handle loading
  setTimeout(() => {
    load(customerId, language, params, node, () => {
      node.removeChild(loader);
    });
  }, 25);
});
